/**
 * Current Performance Screen (Step 3)
 * Monthly reporting periods. Units per month, Price, COGS, CAC, Churn.
 * Real-time panel: Growth rate, Revenue, Expense, Value Ratio.
 */
import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Card, Row, Col, Button, Typography } from 'antd';
import { LeftOutlined, RightOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getProductDomainSession,
  getProductDomainPhaseData,
  upsertProductDomainPhaseData,
} from '../../services/productDomainSessionApi';
import type { ProductDomainSession } from '../../services/productDomainSessionApi';

const { Text } = Typography;

const COLUMNS_VISIBLE = 3;
const MAX_PERIODS = 12;
const COLUMN_WIDTH_PX = 120;
const COLUMN_GAP_PX = 16;

function getMonthLabel(monthsAgo: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - monthsAgo);
  return d.toLocaleString('default', { month: 'short', year: 'numeric' });
}

interface CurrentPerformanceScreenProps {
  sessionId: string;
  onNext: () => void;
}

export const CurrentPerformanceScreen: React.FC<CurrentPerformanceScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [session, setSession] = useState<ProductDomainSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [periodCount, setPeriodCount] = useState(3);
  const [scrollOffset, setScrollOffset] = useState(0);

  const isRecurring = session?.product_type === 'recurring';

  useEffect(() => {
    const load = async () => {
      const [s, phaseData] = await Promise.all([
        getProductDomainSession(sessionId),
        getProductDomainPhaseData(sessionId, 'financial_baseline'),
      ]);
      setSession(s);
      if (phaseData?.phase_data?.financial_baseline) {
        const byQ: Record<string, number> = {};
        let maxPeriod = 0;
        for (const item of phaseData.phase_data.financial_baseline) {
          if (item.value_number != null) {
            byQ[item.question_id] = item.value_number;
            const m = item.question_id.match(/^units_period_(\d+)$/);
            if (m) maxPeriod = Math.max(maxPeriod, parseInt(m[1], 10));
          }
        }
        const initial: Record<string, number> = {};
        for (let i = 1; i <= Math.max(3, maxPeriod); i++) {
          initial[`units_period_${i}`] = byQ[`units_period_${i}`] ?? 0;
        }
        form.setFieldsValue({
          ...initial,
          price_current: byQ.price_current,
          cogs_current: byQ.cogs_current,
          cac_current: byQ.cac_current,
          churn_rate_current: byQ.churn_rate_current,
        });
        setPeriodCount(Math.max(3, maxPeriod));
      }
      setLoading(false);
    };
    load();
  }, [sessionId, form]);

  const values = Form.useWatch([], form) || {};
  const u1 = values.units_period_1 ?? 0;
  const u2 = values.units_period_2 ?? 0;
  const price = values.price_current ?? 0;
  const cogs = values.cogs_current ?? 0;
  const cac = values.cac_current ?? 0;
  const churn = values.churn_rate_current ?? 0;

  const revenue = u1 * price;
  const expensePerUnit = cogs + cac;
  const expense = u1 * expensePerUnit;
  const valueRatio = expense > 0 ? revenue / expense : 0;
  const growthRate = u2 > 0 ? (u1 - u2) / u2 : null;

  const visibleStart = scrollOffset;
  const visibleEnd = Math.min(scrollOffset + COLUMNS_VISIBLE, periodCount);
  const canScrollLeft = scrollOffset > 0;
  const canScrollRight = scrollOffset + COLUMNS_VISIBLE < periodCount;
  const canAddMonth = periodCount < MAX_PERIODS;

  const handleSaveAndNext = async () => {
    const v = await form.validateFields();
    const items: Array<{ question_id: string; value_type: 'number'; value_number: number }> = [];
    for (let i = 1; i <= periodCount; i++) {
      const val = v[`units_period_${i}`];
      if (val != null) items.push({ question_id: `units_period_${i}`, value_type: 'number', value_number: val });
    }
    items.push(
      { question_id: 'price_current', value_type: 'number', value_number: v.price_current },
      { question_id: 'cogs_current', value_type: 'number', value_number: v.cogs_current },
      { question_id: 'cac_current', value_type: 'number', value_number: v.cac_current },
    );
    if (isRecurring && v.churn_rate_current != null) {
      items.push({ question_id: 'churn_rate_current', value_type: 'number', value_number: v.churn_rate_current });
    }
    setSaving(true);
    const res = await upsertProductDomainPhaseData(sessionId, 'financial_baseline', items);
    setSaving(false);
    if (res?.ok) onNext();
  };

  const handleAddMonth = () => {
    if (periodCount >= MAX_PERIODS) return;
    const next = periodCount + 1;
    form.setFieldsValue({ [`units_period_${next}`]: 0 });
    setPeriodCount(next);
    setScrollOffset(Math.max(0, next - COLUMNS_VISIBLE));
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Reporting periods are <strong>monthly</strong>. Enter units sold (or delivered) for each month.
      </Typography.Paragraph>

      <Row gutter={24}>
        <Col span={14}>
          <Card
            title="Units by month"
            extra={
              canAddMonth && (
                <Button type="link" size="small" icon={<PlusOutlined />} onClick={handleAddMonth}>
                  Add preceding month
                </Button>
              )
            }
          >
            <Form form={form} layout="vertical">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  disabled={!canScrollLeft}
                  onClick={() => setScrollOffset((o) => Math.max(0, o - 1))}
                  aria-label="Show newer months"
                />
                <div style={{ flex: 1, overflow: 'hidden', minWidth: COLUMNS_VISIBLE * COLUMN_WIDTH_PX }}>
                  <div
                    style={{
                      display: 'flex',
                      gap: COLUMN_GAP_PX,
                      transform: `translateX(-${Math.max(0, periodCount - COLUMNS_VISIBLE - scrollOffset) * (COLUMN_WIDTH_PX + COLUMN_GAP_PX)}px)`,
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    {Array.from({ length: periodCount }, (_, i) => periodCount - i).map((idx) => (
                      <Form.Item
                        key={idx}
                        name={`units_period_${idx}`}
                        label={getMonthLabel(idx)}
                        rules={idx <= 3 ? [{ required: true }] : []}
                        style={{ flex: `0 0 ${COLUMN_WIDTH_PX}px`, marginBottom: 0 }}
                      >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="Units" />
                      </Form.Item>
                    ))}
                  </div>
                </div>
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  disabled={!canScrollRight}
                  onClick={() => setScrollOffset((o) => Math.min(periodCount - COLUMNS_VISIBLE, o + 1))}
                  aria-label="Show older months"
                />
              </div>
              {periodCount > COLUMNS_VISIBLE && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Showing {visibleEnd - visibleStart} of {periodCount} months. Use arrows to scroll.
                </Text>
              )}
            </Form>
          </Card>
          <Card title="Price, COGS, CAC" style={{ marginTop: 16 }}>
            <Form form={form} layout="vertical">
              <Form.Item name="price_current" label="Price" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="cogs_current" label="COGS" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="cac_current" label="CAC" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              {isRecurring && (
                <Form.Item name="churn_rate_current" label="Churn rate">
                  <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                </Form.Item>
              )}
            </Form>
          </Card>
          <Button type="primary" onClick={handleSaveAndNext} loading={saving} style={{ marginTop: 16 }}>
            Save & Next
          </Button>
        </Col>
        <Col span={10}>
          <Card title="Live Summary">
            <p><Text type="secondary">Growth rate</Text><br /><Text strong>{growthRate != null ? `${(growthRate * 100).toFixed(1)}%` : '—'}</Text></p>
            <p><Text type="secondary">Revenue</Text><br /><Text strong>{revenue.toLocaleString()}</Text></p>
            <p><Text type="secondary">Expense</Text><br /><Text strong>{expense.toLocaleString()}</Text></p>
            <p><Text type="secondary">Value Ratio</Text><br /><Text strong>{valueRatio.toFixed(2)}</Text></p>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
